<template>
  <section v-if="hasContent" class="detail-section">
    <div v-if="!hideHeader && sectionTitle" class="detail-section__header">
      <h3 class="detail-section__title">{{ sectionTitle }}</h3>
      <span class="detail-section__line" />
    </div>
    <p
      v-if="!hideHeader && sectionDescription"
      class="detail-section__subtitle"
      v-html="sectionDescription"
    />

    <div class="detail-section__grid" :class="layoutClass">
      <article
        v-for="item in contentItems"
        :key="item.id"
        class="detail-section__card"
        :class="{
          'detail-section__card--clickable': isItemClickable(item),
          'detail-section__card--horizontal': isSingleLayout
        }"
        @click="handleItemClick(item)"
      >
        <div v-if="itemImage(item)" class="detail-section__card-thumb">
          <img :src="itemImage(item)" :alt="item.name || ''" />
        </div>
        <div class="detail-section__card-body">
          <h4 v-if="item.name" class="detail-section__card-title text-line-2">{{ item.name }}</h4>

          <dl v-if="isSingleLayout && itemAttributes(item).length" class="detail-section__attrs">
            <div
              v-for="attr in itemAttributes(item)"
              :key="attr.label"
              class="detail-section__attr-row"
            >
              <dt>{{ attr.label }}</dt>
              <dd class="text-line-2">{{ attr.value }}</dd>
            </div>
          </dl>

          <p
            v-else-if="itemSummary(item)"
            class="detail-section__card-desc text-line-2"
          >
            {{ itemSummary(item) }}
          </p>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import {
  mediaUrl,
  resolveExhibitionItemPath,
  stripExhibitionItemText,
  parseExhibitionItemAttributes
} from '@/api/exhibition'

export default {
  name: 'DetailSectionContent',
  props: {
    section: {
      type: Object,
      required: true
    },
    hideHeader: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    configure() {
      return (this.section && this.section.configure) || {}
    },
    sectionTitle() {
      return this.configure.title || this.section.title || ''
    },
    sectionDescription() {
      return this.configure.description || ''
    },
    contentItems() {
      return this.configure.items || []
    },
    styleLayout() {
      const layout = this.configure.styleLayout
      return layout == null ? 0 : layout
    },
    isSingleLayout() {
      return this.styleLayout === 0
    },
    layoutClass() {
      if (this.styleLayout === 1) return 'detail-section__grid--double'
      if (this.styleLayout === 2) return 'detail-section__grid--four'
      return 'detail-section__grid--single'
    },
    hasContent() {
      if (this.hideHeader) {
        return this.contentItems.length > 0
      }
      return this.sectionTitle || this.sectionDescription || this.contentItems.length > 0
    }
  },
  methods: {
    itemImage(item) {
      return mediaUrl(item.cover)
    },
    itemSummary(item) {
      return stripExhibitionItemText(item.description)
    },
    itemAttributes(item) {
      return parseExhibitionItemAttributes(item)
    },
    isItemClickable(item) {
      return Boolean(resolveExhibitionItemPath(item))
    },
    handleItemClick(item) {
      const path = resolveExhibitionItemPath(item)
      if (!path) return
      if (/^https?:\/\//i.test(path)) {
        window.open(path, '_blank')
        return
      }
      this.$router.push(path)
    }
  }
}
</script>

<style lang="scss" scoped>
.detail-section {
  margin-bottom: $spacing-3xl;

  &__header {
    display: flex;
    align-items: center;
    gap: $spacing-base;
    margin-bottom: $spacing-sm;
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(22);
    font-weight: 700;
    color: $color-brown-dark;
    letter-spacing: 2px;
    flex-shrink: 0;
  }

  &__line {
    flex: 1;
    height: 1px;
    background: $color-border;
  }

  &__subtitle {
    font-size: rem(13);
    color: $color-text-light;
    margin-bottom: $spacing-base;
    line-height: 1.6;
    white-space: pre-wrap;
  }

  &__grid {
    display: grid;
    gap: $spacing-base;

    &--single {
      grid-template-columns: 1fr;
    }

    &--double {
      grid-template-columns: repeat(2, 1fr);
    }

    &--four {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    background: $color-white;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba($color-brown-dark, 0.06);
    transition: box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
      box-shadow: 0 4px 18px rgba($color-brown-dark, 0.1);
    }

    &--clickable {
      cursor: pointer;

      &:hover {
        box-shadow: 0 6px 24px rgba($color-brown-dark, 0.12);
        transform: translateY(rem(-2));

        .detail-section__card-thumb img {
          transform: scale(1.03);
        }
      }
    }

    &--horizontal {
      flex-direction: row;
      align-items: stretch;
    }
  }

  &__card-thumb {
    height: rem(160);
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: grayscale(0.35) sepia(0.1);
      transition: transform 0.25s ease;
    }
  }

  &__card-body {
    padding: $spacing-sm $spacing-base $spacing-base;
    min-width: 0;
    flex: 1;
  }

  &__card-title {
    font-family: $font-serif;
    font-size: rem(15);
    font-weight: 700;
    color: $color-brown-dark;
    margin-bottom: $spacing-xs;
    line-height: 1.5;
  }

  &__card-desc {
    font-size: rem(13);
    line-height: 1.5;
    color: $color-text-light;
  }

  &__attrs {
    margin: 0;
  }

  &__attr-row {
    display: flex;
    gap: $spacing-xs;
    font-size: rem(13);
    line-height: 1.6;

    & + & {
      margin-top: rem(4);
    }

    dt {
      flex-shrink: 0;
      min-width: rem(72);
      color: $color-text-light;
    }

    dd {
      margin: 0;
      flex: 1;
      min-width: 0;
      color: $color-text;
    }
  }
}

.detail-section__grid--single {
  .detail-section__card-thumb {
    width: rem(180);
    height: rem(140);
  }

  .detail-section__card-body {
    padding: $spacing-base;
  }

  .detail-section__card-title {
    margin-bottom: $spacing-sm;
  }
}

.detail-section__grid--double {
  .detail-section__card-thumb {
    height: rem(300);
  }

  .detail-section__card-body {
    padding: $spacing-sm $spacing-base;
  }

  .detail-section__card-title {
    font-size: rem(14);
    margin-bottom: rem(4);
  }

  .detail-section__card-desc {
    font-size: rem(12);
  }
}

.detail-section__grid--four {
  .detail-section__card-thumb {
    height: rem(96);
  }

  .detail-section__card-body {
    padding: $spacing-xs $spacing-sm $spacing-sm;
  }

  .detail-section__card-title {
    font-size: rem(13);
  }

  .detail-section__card-desc {
    font-size: rem(12);
  }
}

@media (max-width: 960px) {
  .detail-section__grid--four,
  .detail-section__grid--double {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .detail-section__grid--four,
  .detail-section__grid--double,
  .detail-section__grid--single {
    grid-template-columns: 1fr;
  }

  .detail-section__grid--double .detail-section__card-thumb,
  .detail-section__grid--four .detail-section__card-thumb {
    height: rem(120);
  }

  .detail-section__grid--single .detail-section__card--horizontal {
    flex-direction: column;
  }

  .detail-section__grid--single .detail-section__card-thumb {
    width: 100%;
    height: rem(160);
  }
}
</style>
